import { error } from '@ali/re-utils';
import { hasOwn, splitKey } from '../utils';
import Types from '../types';

export default class MMap {
  constructor(data, owner) {
    this.owner = owner;
    this.rawData = data || {};
  }

  init() {
    if (this.inited) return;
    this.inited = true;
    this.maps = {};
    const data = this.rawData;
    const entries = Object.keys(data).sort();
    let key;
    for (let i = 0, l = entries.length; i < l; i++) {
      key = entries[i];
      this.maps[key] = this.owner.create(data[key]);
    }
    this.entries = entries;
    this.rawData = null;
  }

  setData(data, merge, targetKeys) {
    this.init();
    const releaseChange = this.owner.changeCapture();
    const maps = merge ? this.maps : {};
    const entries = this.entries.slice(0);
    let changed = false;
    for (const key in data) {
      if (hasOwn(data, key)) {
        const raw = data[key];
        const i = entries.indexOf(key);
        if (i > -1) {
          const orig = this.maps[key];
          entries.splice(i, 1);
          if (orig.canReceive(raw)) {
            if (orig.setData(raw, merge)) {
              changed = true;
            }
            maps[key] = orig;
            continue;
          }

          orig.purge();
          changed = true;
        } else {
          changed = true;
        }

        maps[key] = this.owner.create(raw);
      }
    }

    if (Object.keys(data).length !== Object.keys(this.maps).length) {
      changed = true;
    }

    this.maps = maps;
    if (changed) {
      this.entries = Object.keys(maps).sort();
      this.owner.markChange();
      releaseChange(targetKeys, false);
    } else {
      // 虽然没有变化，也要释放含有的锁，防止空数据源的情况
      releaseChange(undefined, false);
    }
    return changed;
  }

  into(data, type, merge = true, targetKeys) {
    if (!type) {
      type = Types.test(data);
    }

    switch (type) {
      case Types.NULL:
        if (!merge) {
          this.clear();
        }
        break;
      case Types.MAP:
        return this.setData(data, merge, targetKeys);
      default:
        error(`Map cannot receive data of type "${type}"`, data);
    }
    return true;
  }

  get(key) {
    const keyPath = splitKey(key);
    if (!keyPath) {
      return null;
    }

    if (keyPath[1]) {
      const sup = this.owner.getOwner();
      return sup ? sup.get(keyPath[2]) : null;
    }

    this.init();

    const curKey = keyPath[3];
    const nestPath = keyPath[4];
    const item = curKey ? this.maps[curKey] : this.owner;

    if (!item || !nestPath) {
      return item;
    }

    return item.get(nestPath);
  }

  set(key, data, merge) {
    let changed = true;
    this.init();
    const releaseChange = this.owner.changeCapture();
    if (this.has(key)) {
      const type = Types.test(data);
      const orig = this.maps[key];
      if (type === Types.MUTABLE) {
        if (data !== orig) {
          orig.purge();
          this.maps[key] = data;
          data.setOwner(this.owner);
        }
      } else if (!orig.canReceiveType(type)) {
        orig.purge();
        this.maps[key] = this.owner.create(data);
      } else {
        changed = orig.setData(data, merge);
      }
    } else {
      const item = this.owner.create(data);
      this.maps[key] = item;
      this.entries.push(key);
      this.entries = this.entries.sort();
    }
    if (changed !== false) {
      this.owner.markChange();
    }
    releaseChange(changed ? [key] : [], false);
  }

  bind(key, state) {
    this.init();
    if (this.has(key)) {
      const enable = state.disableEmitter();
      // got runtime instance data before bind, so should merge after
      // move to x-props
      // if (this.owner.getValue('__mergeAfterBind')) {
      //   state.mergeData(this.maps[key].toJS());
      // }
      this.maps[key].purge();
      enable();
      state.saveResetPoint();
      this.maps[key] = state;
    } else {
      this.maps[key] = state;
      this.entries.push(key);
      this.entries = this.entries.sort();
    }
    this.owner.markChange();
  }

  has(key) {
    this.init();
    return this.entries.indexOf(key) > -1;
  }

  clear() {
    if (this.entries) {
      this.entries.forEach((key) => {
        this.maps[key].purge();
      });
      if (this.entries.length > 0) {
        this.owner.markChange();
      }
    }
    this.entries = [];
    this.maps = {};
    this.rawData = {};
    this.owner.releaseChange();
  }

  keyOf(item) {
    this.init();
    let key;
    for (let i = 0, l = this.entries.length; i < l; i++) {
      key = this.entries[i];
      if (this.maps[key] === item) {
        return key;
      }
    }
    return null;
  }

  forEach(fn) {
    this.init();
    this.entries.forEach(key => fn(this.maps[key], key, this.maps));
  }

  every(fn) {
    this.init();
    return this.entries.every(key => fn(this.maps[key], key, this.maps));
  }

  some(fn) {
    this.init();
    return this.entries.some(key => fn(this.maps[key], key, this.maps));
  }

  map(fn) {
    this.init();
    return this.entries.map(key => fn(this.maps[key], key, this.maps));
  }

  removeItem(item) {
    this.init();
    let key;
    let index = -1;
    for (let i = 0, l = this.entries.length; i < l; i++) {
      key = this.entries[i];
      if (this.maps[key] === item) {
        index = i;
        break;
      }
    }
    if (index < 0) return;

    item.purge();
    this.entries.splice(index, 1);
    delete this.maps[key];
    this.owner.statChange();
  }

  toJS(force) {
    this.init();
    const maps = {};
    this.entries.forEach((key) => {
      maps[key] = this.maps[key].toJS(force);
    });
    return maps;
  }

  size() {
    this.init();
    return this.entries.length;
  }
}
